import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  type DocumentData,
  type QueryConstraint,
  type QueryDocumentSnapshot
} from 'firebase/firestore';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getFirestoreDb } from '@/lib/firebase';

export const DEFAULT_PAGE_SIZE = 25;

const EMPTY_CONSTRAINTS: QueryConstraint[] = [];

export type UseFirestorePageQueryOptions<T> = {
  orgId: string | null;
  /** Path segments under organizations/{orgId}/ */
  collectionPath: string | string[];
  orderByField: string;
  orderDirection?: 'asc' | 'desc';
  pageSize?: number;
  mapDoc: (id: string, data: DocumentData) => T;
  /** Extra query constraints (where, etc.) */
  constraints?: QueryConstraint[];
  enabled?: boolean;
};

export type UseFirestorePageQueryResult<T> = {
  rows: T[];
  loading: boolean;
  error: string | null;
  pageIndex: number;
  pageSize: number;
  hasNext: boolean;
  hasPrev: boolean;
  nextPage: () => void;
  prevPage: () => void;
  setPageSize: (size: number) => void;
  refresh: () => void;
};

function collectionRef(orgId: string, collectionPath: string | string[]) {
  const segments = Array.isArray(collectionPath) ? collectionPath : [collectionPath];
  return collection(getFirestoreDb(), 'organizations', orgId, ...segments);
}

export function useFirestorePageQuery<T>({
  orgId,
  collectionPath,
  orderByField,
  orderDirection = 'desc',
  pageSize: initialPageSize = DEFAULT_PAGE_SIZE,
  mapDoc,
  constraints: constraintsProp,
  enabled = true
}: UseFirestorePageQueryOptions<T>): UseFirestorePageQueryResult<T> {
  const constraints = constraintsProp ?? EMPTY_CONSTRAINTS;
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [hasNext, setHasNext] = useState(false);

  const cursorsRef = useRef<(QueryDocumentSnapshot | null)[]>([null]);
  const fetchGenRef = useRef(0);
  const mapDocRef = useRef(mapDoc);
  mapDocRef.current = mapDoc;
  const constraintsRef = useRef(constraints);
  constraintsRef.current = constraints;

  const pathKey = Array.isArray(collectionPath) ? collectionPath.join('/') : collectionPath;
  const constraintsKey = constraints.map((c) => c.type).join(',');

  const fetchPage = useCallback(
    async (index: number, size: number) => {
      if (!orgId || !enabled) {
        setRows([]);
        setHasNext(false);
        setLoading(false);
        setError(null);
        return;
      }
      const gen = ++fetchGenRef.current;
      setLoading(true);
      setError(null);
      try {
        const cursor = cursorsRef.current[index] ?? null;
        const col = collectionRef(orgId, collectionPath);
        const q = query(
          col,
          ...constraintsRef.current,
          orderBy(orderByField, orderDirection),
          ...(cursor ? [startAfter(cursor)] : []),
          limit(size)
        );
        const snap = await getDocs(q);
        if (gen !== fetchGenRef.current) return;

        const mapped = snap.docs.map((d) => mapDocRef.current(d.id, d.data()));
        setRows(mapped);
        setHasNext(snap.docs.length === size);
        if (snap.docs.length > 0) {
          cursorsRef.current[index + 1] = snap.docs[snap.docs.length - 1]!;
        }
      } catch (e) {
        if (gen !== fetchGenRef.current) return;
        setError(e instanceof Error ? e.message : 'fetch_failed');
        setRows([]);
        setHasNext(false);
      } finally {
        if (gen === fetchGenRef.current) setLoading(false);
      }
    },
    [orgId, pathKey, orderByField, orderDirection, enabled]
  );

  useEffect(() => {
    cursorsRef.current = [null];
    setPageIndex(0);
  }, [orgId, pathKey, orderByField, orderDirection, pageSize, constraintsKey, enabled]);

  useEffect(() => {
    void fetchPage(pageIndex, pageSize);
  }, [fetchPage, pageIndex, pageSize, constraintsKey]);

  const nextPage = useCallback(() => {
    if (!hasNext || loading) return;
    setPageIndex((p) => p + 1);
  }, [hasNext, loading]);

  const prevPage = useCallback(() => {
    if (pageIndex <= 0 || loading) return;
    setPageIndex((p) => p - 1);
  }, [pageIndex, loading]);

  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    cursorsRef.current = [null];
    setPageIndex(0);
  }, []);

  const refresh = useCallback(() => {
    cursorsRef.current = [null];
    void fetchPage(pageIndex, pageSize);
  }, [fetchPage, pageIndex, pageSize]);

  return {
    rows,
    loading,
    error,
    pageIndex,
    pageSize,
    hasNext,
    hasPrev: pageIndex > 0,
    nextPage,
    prevPage,
    setPageSize,
    refresh
  };
}
